/**
 * Token utility functions for authentication management
 */

export const TOKEN_KEY = "authToken";

/**
 * Get token from localStorage
 */
export const getTokenFromStorage = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (error) {
    console.error("Error getting token from localStorage:", error);
    return null;
  }
};

/**
 * Set token in localStorage
 */
export const setTokenInStorage = (token: string): void => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch (error) {
    console.error("Error setting token in localStorage:", error);
  }
};

/**
 * Remove token from localStorage
 */
export const removeTokenFromStorage = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch (error) {
    console.error("Error removing token from localStorage:", error);
  }
};

/**
 * Check if token exists in localStorage
 */
export const hasTokenInStorage = (): boolean => {
  return getTokenFromStorage() !== null;
};

/**
 * Validate token format (basic validation)
 */
export const isValidTokenFormat = (token: string): boolean => {
  return typeof token === "string" && token.length > 0;
};

/** Returns the JWT expiration time in milliseconds, or null for a malformed token. */
export const getTokenExpiration = (token: string): number | null => {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;

    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = JSON.parse(atob(normalized));
    return typeof decoded.exp === "number" ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
};

/**
 * Get token info for debugging
 */
export const getTokenInfo = () => {
  const token = getTokenFromStorage();
  return {
    exists: !!token,
    length: token?.length || 0,
    isValidFormat: token ? isValidTokenFormat(token) : false,
    preview: token ? `${token.substring(0, 10)}...` : null,
    isJWT: token ? token.includes(".") && token.split(".").length === 3 : false,
    isSessionToken: token ? token.startsWith("session_") : false,
  };
};
